import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo, useRef } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { injectIntl } from 'react-intl';
import { isNullOrUndefined } from 'utils/object-utils';
import { shouldBreakWord } from 'constants/common/multi-line-ellipsis-constants';
import ReactDOM from 'react-dom';

const TOOLTIP_BLOCK = 'tooltip';
/** must wrap actual html elements not react components */

const Tooltip = ({
  id,
  text,
  position,
  className,
  children,
  isNotInternationalized,
  active = true,
  useDefault,
  intl,
  event,
  tooltipOnHideFunction,
  delay,
  clickable,
  offset,
  usePortal = true,
}) => {
  const reactTooltipRef = useRef(null);

  const _id = `${id}-tooltip`;
  if (isNullOrUndefined(id) || isNullOrUndefined(text)) {
    return children;
  }
  const childrenWithProps = React.Children.map(children, (child) =>
    React.cloneElement(child, {
      id: _id,
      'data-tooltip-id': _id,
      'data-for': _id,
      'data-tip': 'data-tip',
    }),
  );

  const _displayText = () => {
    if (isNotInternationalized) {
      return text;
    } else if (typeof text === 'string') {
      return intl.formatMessage({ id: text });
    } else if (typeof text === 'object') {
      if (text.defaultMessage) {
        return intl.formatMessage(
          { id: text.id, defaultMessage: text.defaultMessage },
          text.values,
        );
      }
      return intl.formatMessage({ id: text.id }, text.values);
    }
    return null;
  };

  const defaultTooltipChild = React.Children.map(children, (child) =>
    React.cloneElement(child, { id: _id, title: _displayText() }),
  );

  if (useDefault) {
    return <React.Fragment>{defaultTooltipChild}</React.Fragment>;
  } else if (active) {
    const tooltipElement = (
      <ReactTooltip
        className={classNames(
          TOOLTIP_BLOCK,
          'tooltip--half-viewport',
          className,
          shouldBreakWord(_displayText())
            ? `${TOOLTIP_BLOCK}--break-word`
            : null,
        )}
        id={_id}
        place={position || 'top'}
        ref={reactTooltipRef}
        event={event ? event : null}
        //if event is changed, isCapture is neccesary to allow onClick events of children
        isCapture={event ? true : null}
        afterHide={tooltipOnHideFunction ? tooltipOnHideFunction : null}
        delayHide={delay ? delay : 0}
        clickable={clickable}
        offset={offset}
      >
        {_displayText()}
      </ReactTooltip>
    );

    return (
      <>
        {usePortal
          ? ReactDOM.createPortal(
              tooltipElement,
              document.getElementById('tooltip-root'),
            )
          : tooltipElement}
        {childrenWithProps}
      </>
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
};

Tooltip.propTypes = {
  /** REQUIRED if passing a tooltip, no tooltip attributes applied to children if not present, Unique String id for tool tip*/
  id: PropTypes.string,
  /** REQUIRED if passing a tooltip, no tooltip attributes applied to children if not present, String copy for the tool tip*/
  text: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      values: PropTypes.object,
    }),
    PropTypes.node,
  ]),
  /** Position of tooltip: right, left, top, bottom */
  position: PropTypes.string,
  /** String custom class */
  className: PropTypes.string,
  /** Boolean representing if the tool tip accepts an internationalized id or a string */
  isNotInternationalized: PropTypes.bool,
  /** Boolean representing if a tool tip should render or just its child. useful for conditionally adding tooltips to components.*/
  active: PropTypes.bool,
  /** indicates if the tooltip should be browser based using the "title" attribute or a custom tooltip */
  useDefault: PropTypes.bool,
  /* indicates if the tooltip needs to work with different event other than hover */
  event: PropTypes.string,
  /* function executed when tooltip hides */
  tooltipOnHideFunction: PropTypes.func,
  /* delay in hiding the tooltip */
  delay: PropTypes.number,
  /* boolean value that allows the tooltip to be clickable */
  clickable: PropTypes.bool,
};

export default memo(injectIntl(Tooltip));
